import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface CampaignSchedule {
  id: number
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
  start_time: string // HH:MM:SS
  end_time: string
  timezone: string
}

export interface UpdateCampaignSchedulePayload {
  monday?: boolean
  tuesday?: boolean
  wednesday?: boolean
  thursday?: boolean
  friday?: boolean
  saturday?: boolean
  sunday?: boolean
  start_time?: string
  end_time?: string
  timezone?: string
}

// Query keys
export const campaignScheduleKeys = {
  all: ['campaign-schedule'] as const,
  byCampaign: (campaignId: number) =>
    [...campaignScheduleKeys.all, 'campaign', campaignId] as const,
}

// API calls
const getCampaignSchedule = async (
  campaignId: number
): Promise<CampaignSchedule | null> => {
  return baseAPICall<CampaignSchedule | null>(() =>
    clientInstance.get(`/emailbison/campaigns/${campaignId}/schedule/`)
  )
}

const updateCampaignSchedule = async (
  campaignId: number,
  payload: UpdateCampaignSchedulePayload
): Promise<CampaignSchedule> => {
  return baseAPICall<CampaignSchedule>(() =>
    clientInstance.put(`/emailbison/campaigns/${campaignId}/schedule/`, payload)
  )
}

// Hooks
export const useGetCampaignSchedule = (campaignId: number, enabled = true) => {
  return useQuery({
    queryKey: campaignScheduleKeys.byCampaign(campaignId),
    queryFn: () => getCampaignSchedule(campaignId),
    enabled: enabled && campaignId > 0,
  })
}

export const useUpdateCampaignSchedule = (campaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdateCampaignSchedulePayload) =>
      updateCampaignSchedule(campaignId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(
        campaignScheduleKeys.byCampaign(campaignId),
        data
      )
    },
  })
}
